import { Component } from '@theme/component';

/**
 * A lightweight, scroll-snap product carousel controlled with vanilla JS.
 *
 * Moves the scroll track one card at a time via prev/next buttons and keeps
 * those controls enabled/disabled based on the current scroll position.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The horizontally scrolling track of cards.
 * @property {HTMLButtonElement} [prev] - The previous button.
 * @property {HTMLButtonElement} [next] - The next button.
 *
 * @extends {Component<Refs>}
 */
class PetCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('resize', this.#onScroll, { passive: true, signal });

    this.#updateControls();
  }

  disconnectedCallback() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  /** Scrolls to the previous card. */
  scrollPrev = () => this.#scrollByCards(-1);

  /** Scrolls to the next card. */
  scrollNext = () => this.#scrollByCards(1);

  /**
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #scrollByCards(direction) {
    this.refs.track.scrollBy({ left: this.#step() * direction, behavior: 'smooth' });
  }

  /**
   * Distance of one card plus the gap between cards.
   * @returns {number}
   */
  #step() {
    const { track } = this.refs;
    const card = track.querySelector('[data-pet-carousel-item]');
    if (!(card instanceof HTMLElement)) return track.clientWidth;

    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.offsetWidth + gap;
  }

  #onScroll = () => {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateControls());
  };

  #updateControls() {
    const { track, prev, next } = this.refs;
    const maxScroll = track.scrollWidth - track.clientWidth - 1;
    const overflowing = track.scrollWidth > track.clientWidth + 1;

    if (prev) prev.disabled = !overflowing || track.scrollLeft <= 0;
    if (next) next.disabled = !overflowing || track.scrollLeft >= maxScroll;
  }
}

if (!customElements.get('pet-carousel')) {
  customElements.define('pet-carousel', PetCarousel);
}
